import { Component, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { sanitizeText } from 'src/app/common/input-utils';
import type { OficinaOption } from '../../services/informacion-tareas.types';

/**
 * Componente dumb: filtros del modulo Informacion de Tareas.
 *
 * Legacy: InformacionTareasViewImpl (seccion de filtros)
 */
@Component({
  selector: 'app-filtros-informacion-tareas',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    DatePickerModule,
    DropdownModule,
    FieldsetModule,
    InputTextModule,
    TooltipModule
  ],
  template: `
    <p-fieldset legend="Informacion de Tareas" [toggleable]="true" styleClass="mb-4">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Orientación de servicio</label>
          <input
            pInputText
            [ngModel]="filtroOrden()"
            (ngModelChange)="onFiltroOrdenChange($event)"
            maxlength="50"
            class="w-full"
            (keyup.enter)="buscar.emit()" />
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha entrega desde *</label>
          <p-datepicker
            [ngModel]="fechaEntregaDesde()"
            (ngModelChange)="fechaEntregaDesdeChange.emit($event)"
            dateFormat="yy/mm/dd"
            [showIcon]="true"
            appendTo="body"
            styleClass="w-full">
          </p-datepicker>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Fecha entrega hasta *</label>
          <p-datepicker
            [ngModel]="fechaEntregaHasta()"
            (ngModelChange)="fechaEntregaHastaChange.emit($event)"
            dateFormat="yy/mm/dd"
            [showIcon]="true"
            appendTo="body"
            styleClass="w-full">
          </p-datepicker>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Oficina</label>
          <p-dropdown
            [options]="oficinas()"
            [ngModel]="filtroOficina()"
            (ngModelChange)="filtroOficinaChange.emit($event)"
            optionLabel="label"
            optionValue="value"
            [filter]="true"
            filterBy="label"
            filterPlaceholder="Buscar oficina..."
            [resetFilterOnHide]="true"
            emptyFilterMessage="Sin coincidencias"
            appendTo="body"
            styleClass="w-full">
          </p-dropdown>
        </div>

        <div class="col-span-full lg:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Consultor</label>
          <div class="flex gap-2">
            <input pInputText [value]="nombreAsesor()" readonly class="flex-1" />
            @if (!esAsesor()) {
              <button
                pButton
                icon="pi pi-search"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="primary"
                size="small"
                pTooltip="Buscar asesor"
                tooltipPosition="left"
                (click)="abrirBuscadorAsesor.emit()">
              </button>
              <button
                pButton
                icon="pi pi-times"
                class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                severity="danger"
                size="small"
                pTooltip="Limpiar asesor"
                tooltipPosition="left"
                (click)="limpiarAsesor.emit()"
                [disabled]="!tieneAsesorSeleccionado()">
              </button>
            }
          </div>
        </div>

        <div class="col-span-full lg:col-span-3">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cliente</label>
          <div class="flex gap-2">
            <input pInputText [value]="nombreBeneficiario()" readonly class="flex-1" />
            <button
              pButton
              icon="pi pi-search"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              severity="primary"
              size="small"
              pTooltip="Buscar beneficiario"
              tooltipPosition="left"
              (click)="abrirBuscadorBeneficiario.emit()">
            </button>
            <button
              pButton
              icon="pi pi-times"
              class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
              severity="danger"
              size="small"
              pTooltip="Limpiar beneficiario"
              tooltipPosition="left"
              (click)="limpiarBeneficiario.emit()"
              [disabled]="!tieneBeneficiarioSeleccionado()">
            </button>
          </div>
        </div>
      </div>

      <div class="mt-4 flex flex-wrap justify-end sm:justify-start gap-3 w-full">
        <p-button
          label="Limpiar"
          icon="pi pi-eraser"
          severity="secondary"
          [outlined]="true"
          (onClick)="limpiar.emit()">
        </p-button>
        <p-button
          label="Buscar"
          icon="pi pi-search"
          [loading]="consultando()"
          (onClick)="buscar.emit()">
        </p-button>
      </div>
    </p-fieldset>
  `
})
export class FiltrosInformacionTareasComponent {
  oficinas = input<OficinaOption[]>([]);
  esAsesor = input(false);
  consultando = input(false);

  filtroOrden = input('');
  fechaEntregaDesde = input<Date | null>(null);
  fechaEntregaHasta = input<Date | null>(null);
  filtroOficina = input<number | null>(null);
  nombreAsesor = input('');
  nombreBeneficiario = input('');
  tieneAsesorSeleccionado = input(false);
  tieneBeneficiarioSeleccionado = input(false);

  filtroOrdenChange = output<string>();
  fechaEntregaDesdeChange = output<Date | null>();
  fechaEntregaHastaChange = output<Date | null>();
  filtroOficinaChange = output<number | null>();

  buscar = output<void>();
  limpiar = output<void>();
  abrirBuscadorAsesor = output<void>();
  limpiarAsesor = output<void>();
  abrirBuscadorBeneficiario = output<void>();
  limpiarBeneficiario = output<void>();

  onFiltroOrdenChange(value: string | null | undefined): void {
    this.filtroOrdenChange.emit(sanitizeText(value));
  }
}
